import React, { forwardRef, useCallback, useRef, useState, useImperativeHandle } from "react";
import { RCCButton, RCCChartGanttDataType, RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import Accordion from "src/components/Accordion";
import GanttChart from "src/components/Charts/GanttChart";
import Modal from "src/components/Modal";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { IProjectModalGanttRef } from "./interface";

const getMethodUrl = (level?: "phase" | "project" | "program") => {
	if(level === "phase")
		return "ProjectPhase/ViewGantt";
	if(level === "program")
		return "Project/ViewGanttProgram";
	return "Project/ViewGantt";
};


const ModalGantt: React.ForwardRefRenderFunction<IProjectModalGanttRef> = (_, ref) => {
	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();
    
	const { checkIfHasSpecificPermission } = useAuth();

	// Modal details
	const modalDetail = useRef<RCCRefModalProps>(null);
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);

	const [ganttData, setGanttData] = useState<RCCChartGanttDataType[]>([]);

	const openModalGantt = useCallback(async (id: number, title: string, level?: "phase" | "project" | "program") => {
		try{
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: getMethodUrl(level ?? "project"),
				urlParameters: {
					id
				}
			});
			setGanttData(data as any);
			setLoading(false);
			setSubtitle(title);
			modalDetail.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar detalhes"), addToast);
		}
	}, []);

	useImperativeHandle(ref, () => ({
		openModalGantt
	}));

	return (
		<>
			<Modal
				ref={modalDetail}
				header={{
					title: `${translate("Visualizar")} Gantt`,
					subtitle,
					icon: {
						name: "table"
					}
				}}
				footer= {{
					showSaveButton: false,
					cancelButtonText: translate("Fechar")
				}}
			>
				<Accordion
					header={<>
						{translate("Instruções para vincular ações")}
					</>}
				>
					<RCCGrid container item direction="column">
						<Title color="grey" bold>
							{translate("Vincular ações")}: 
						</Title>
						<Title color="grey">
							1º {translate("Dê um clique duplo sobre a barra que representa a ação predecessora. Você vai notar que a borda da barra fica vermelha")}. 
						</Title>
						<Title color="grey">
							2º {translate("Agora clique sobre a barra que representa a ação sucessora")}.
						</Title>
						<Title color="grey">
							{translate("Em caso de sucesso, você vai perceber que uma linha vermelha é desenhada partindo da ação predecessora até sua sucessora")}.
						</Title>
						<Title color="grey">
							{translate("Em caso de erro, será indicado na mensagem de erro")}.
						</Title>
						<Title color="grey" bold>
							{translate("Desvincular ações")}: 
						</Title>
						<Title color="grey">
							{translate("Basta executar o mesmo processo feito para vincular")}.
						</Title>
					</RCCGrid>
				</Accordion>
				<RCCGrid container item alignItems="center" style={{ position: "absolute", bottom: "5rem" }}>
					<RCCTooltip
						color="grey"
						text={translate("Expandir todos")}
					>
						<RCCButton 
							color="grey"
							size="sm"
							onClick={() => {
								setGanttData((prevState) => prevState.map((x) => ({
									...x,
									pOpen: 1
								})));
							}}
							icon={{
								name: "expand"
							}}
							layout="isRounded"
						/>
					</RCCTooltip>
					<RCCTooltip
						color="grey"
						text={translate("Comprimir todos")}
					>
						<RCCButton 
							color="grey"
							size="sm"
							onClick={() => {
								setGanttData((prevState) => prevState.map((x) => ({
									...x,
									pOpen: 0
								})));
							}}
							icon={{
								name: "compress"
							}}
							layout="isRounded"
						/>
					</RCCTooltip>
				</RCCGrid>
				<GanttChart 
					data={ganttData}
					onCreateDependants={async (from: RCCChartGanttDataType, to: RCCChartGanttDataType) => {
						try{
							setLoading(true);

							await RCCServicesApi.putRequest({
								method: "Project/UpdateSubsequentAction",
								body: {
									inb012Inb001Id: from.pID,
									inb012Inb001Id2: to.pID
								}
							});

							setGanttData((prevState) => prevState.map((pState) => {
								if(pState.pID === to.pID)
									return {
										...pState,
										pDepend: !pState.pDepend?.includes(from.pID.toString()) ? pState.pDepend + from.pID.toString() : pState.pDepend?.replace(from.pID.toString(), "")
									};

								return pState;
							}));

							setLoading(false);
						}catch(err){
							setLoading(false);
							standardErrorMessages(err, translate("Erro ao adicionar ação subsequente"), addToast);
						}
					}}
					verifyIfCreateDependants={(data: RCCChartGanttDataType) => data.pClass.includes("gtask")}
					allowCreateDependants={checkIfHasSpecificPermission("actions/panel", PermissionEnum.CREATE_UPDATE)}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(ModalGantt);
